import { useCallback, useMemo } from 'react';
import { savingsService } from '../services/savingsService';
import { migrateSavings, deriveGoal } from '../utils/savingsMigration';

export function useSavings({ householdId, data }) {
  const savings = useMemo(() => migrateSavings(data?.savings), [data?.savings]);

  const goals = useMemo(() => {
    return Object.values(savings.goals || {})
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
      .map(deriveGoal);
  }, [savings]);

  const totals = useMemo(() => {
    const totalTarget = goals.reduce((s, g) => s + (g.target || 0), 0);
    const totalSaved = goals.reduce((s, g) => s + (g.saved || 0), 0);
    const overallProgress = totalTarget > 0 ? Math.min(100, (totalSaved / totalTarget) * 100) : 0;
    return { totalTarget, totalSaved, overallProgress };
  }, [goals]);

  const createGoal = useCallback((fields) => {
    return savingsService.createGoal(householdId, data, fields);
  }, [householdId, data]);

  const updateGoal = useCallback((goalId, patch) => {
    return savingsService.updateGoal(householdId, data, goalId, patch);
  }, [householdId, data]);

  const deleteGoal = useCallback((goalId) => {
    return savingsService.deleteGoal(householdId, data, goalId);
  }, [householdId, data]);

  const contribute = useCallback((goalId, amount, description) => {
    return savingsService.contribute(householdId, data, goalId, amount, description);
  }, [householdId, data]);

  return { goals, totals, createGoal, updateGoal, deleteGoal, contribute };
}
